/**
 * A lot of this is currently unused code.
 * The idea is to migrate all the config functions to here.
 * See the POC PR here - https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/158198/overview
 * But, due to FGing, risk, etc, we won't do it in one go.
 * So the config will be a mix of from here, vs elsewhere for a short period of time.
 */
import React from 'react';
import { MessageDescriptor } from 'react-intl';

import { LoadingState } from '@townsquare/loading-state';
import { fg } from '@townsquare/stat-sig/gate';

const ScreenTypes = [
	'BROWSE_ALL',
	'SEARCH_ALL',
	'SEARCH_PEOPLE',
	'SEARCH_TEAMS',
	'YOUR_TEAMS',
	'BROWSE_DEPARTMENTS',
	'BROWSE_LOCATIONS',
	'BROWSE_JOB_TITLES',
	'BROWSE_KUDOS',
	'BROWSE_AGENTS',
	'SEARCH_AGENTS',
] as const;
export type ScreenType = (typeof ScreenTypes)[number];

type ScreenConfig = {
	fetchCanInviteUserToSite?: boolean;
	filterRestriction?: 'people' | 'teams' | 'kudos';
	headerEnabled?: boolean;
	pageTitle?: (intl: (message: MessageDescriptor) => string) => string;
	searchBarEnabled: boolean;
	searchBarPreviewTQL?: (tql: any) => any;
	showCreateTeamButton?: boolean;
	showInviteUserButton?: (canInviteUserToSite: boolean) => boolean;
	showGiveKudosButton?: boolean;
	showRecentInSearch?: boolean;
	Skeleton?: React.ReactNode;
	type: 'TEAM' | 'PEOPLE' | 'KUDOS' | 'LANDING';
};

type ScreenFeature = keyof ScreenConfig;

// Some values need fallbacks to avoid needing to do fallbacks in place
type ScreenConfigWithDefaults = ScreenConfig &
	Required<
		Pick<
			ScreenConfig,
			| 'searchBarPreviewTQL'
			| 'headerEnabled'
			| 'showInviteUserButton'
			| 'fetchCanInviteUserToSite'
			| 'Skeleton'
			| 'pageTitle'
		>
	>;

const defaultScreenConfig: ScreenConfigWithDefaults = {
	searchBarEnabled: false,
	type: 'LANDING',
	filterRestriction: undefined,
	pageTitle: () => '',
	searchBarPreviewTQL: () => undefined,
	headerEnabled: false,
	showInviteUserButton: canInviteUserToSite => canInviteUserToSite,
	fetchCanInviteUserToSite: true,
	Skeleton: null, // TODO: before enabling this should be BrowseAllSkeleton, but it is causing cyclic dependency issues
};

export const screenConfig: Partial<Record<ScreenType, ScreenConfig>> = {
	BROWSE_ALL: {
		searchBarEnabled: false,
		type: 'LANDING',
		showRecentInSearch: true,
		headerEnabled: false,
		showInviteUserButton: () => false,
	},
	SEARCH_ALL: {
		searchBarEnabled: true,
		type: 'LANDING',
		pageTitle: intl =>
			intl({
				id: 'townsquare.web.directory-header.search-all',
				description: 'Search all screen',
				defaultMessage: 'People and teams',
			}),
		Skeleton: <LoadingState />,
	},
	// SEARCH_PEOPLE, SEARCH_TEAMS, and YOUR_TEAMS redirect to separate views and never render DirectoryHeader
	// They are kept in ScreenTypes array for type validation but have no config entries
	BROWSE_KUDOS: {
		searchBarEnabled: false,
		type: 'KUDOS',
		filterRestriction: 'kudos',
		pageTitle: intl =>
			intl({
				id: 'townsquare.web.directory-header.browse-kudos',
				description: 'Browse kudos screen',
				defaultMessage: 'Kudos',
			}),
		searchBarPreviewTQL: tql => tql,
		headerEnabled: true,
		showInviteUserButton: () => false,
		fetchCanInviteUserToSite: false,
		Skeleton: <LoadingState />,
		showGiveKudosButton: true,
	},
	BROWSE_LOCATIONS: {
		searchBarEnabled: false,
		type: 'PEOPLE',
		showRecentInSearch: true,
		searchBarPreviewTQL: tql => tql,
		Skeleton: <LoadingState />,
		headerEnabled: true,
		showInviteUserButton: () => false,
		pageTitle: intl =>
			intl({
				id: 'townsquare.web.directory-header.browse-locations',
				description: 'Browse locations screen',
				defaultMessage: 'Locations',
			}),
	},
	BROWSE_DEPARTMENTS: {
		searchBarEnabled: false,
		type: 'PEOPLE',
		showRecentInSearch: true,
		searchBarPreviewTQL: tql => tql,
		Skeleton: <LoadingState />,
		headerEnabled: true,
		showInviteUserButton: () => false,
		pageTitle: intl =>
			intl({
				id: 'townsquare.web.directory-header.browse-departments',
				description: 'Browse departments screen',
				defaultMessage: 'Departments',
			}),
	},
	BROWSE_JOB_TITLES: {
		searchBarEnabled: false,
		type: 'PEOPLE',
		showRecentInSearch: true,
		searchBarPreviewTQL: tql => tql,
		Skeleton: <LoadingState />,
		headerEnabled: true,
		showInviteUserButton: () => false,
		pageTitle: intl =>
			intl({
				id: 'townsquare.web.directory-header.browse-job-titles',
				description: 'Browse job titles screen',
				defaultMessage: 'Job titles',
			}),
	},
	BROWSE_AGENTS: {
		searchBarEnabled: false,
		type: 'PEOPLE',
	},
};

const DEFAULT_SCREEN_TYPE: ScreenType = 'BROWSE_ALL';

export function configForScreen<T extends ScreenFeature>(screen: ScreenType, feature: T): ScreenConfigWithDefaults[T] {
	const configMergedWithDefaults = {
		...defaultScreenConfig,
		...screenConfig[screen],
	};
	return configMergedWithDefaults[feature];
}

export const getScreenFromStringOrDefault = (screenKey: string | undefined): ScreenType =>
	screenKey && ScreenTypes.includes(screenKey as ScreenType) ? (screenKey as ScreenType) : DEFAULT_SCREEN_TYPE;

/**
 * This function is used to get a feature gated config for a screen.
 * It will return the config for the screen if the feature gate is enabled.
 * If the feature gate is not enabled, it will return the fallback.
 *
 * @param screen - The screen to get the config for.
 * @param feature - The feature to get the config for.
 * @param fallback - The fallback value to return if the feature gate is not enabled.
 * @returns The config for the screen if the feature gate is enabled, otherwise the fallback.
 */
export function featureGatedConfigForScreenWithFallback<T extends ScreenFeature>(
	screen: ScreenType,
	feature: T,
	fallback: ScreenConfigWithDefaults[T],
): ScreenConfigWithDefaults[T] {
	if (fg('teams-app-centralised-config_two')) {
		return configForScreen(screen, feature);
	}
	return fallback;
}
